import { RINK, type Role } from '../types';
import type { GameState } from './physics';

// When a wall-pin is detected, the bot commits to a "go home" sweep
// for this many ticks before normal AI resumes. Long enough for it to
// disengage (>70 units from the puck), which disarms the pin condition
// and lets the puck drift free along the residual velocity from the
// initial contact.
const SWEEP_LOCK_TICKS = 30;
let sweepLockUntilTick = -1;

// Low-pass filter on the bot's returned target. At low aggro the bot's
// paddle target slewing rate is reduced, simulating slow reaction; at
// high aggro it tracks the computed target nearly instantly.
let smoothedX = RINK.width / 2;
let smoothedY = RINK.height / 2;
let smoothedInit = false;

/**
 * Choose a paddle target for the bot.
 *
 * Strategy:
 *   1. If the puck is in our own DANGER ZONE (near our goal), attack it
 *      regardless of velocity. This is the always-on safety rule that
 *      prevents the bot from hovering "behind" a puck that's already
 *      pinned against its own wall — previously they'd overlap, the
 *      collision response would no-op (d===0), and both got stuck.
 *   2. Else if puck is in our half and coming toward us (or barely
 *      moving), chase it.
 *   3. Else if puck is in our half but moving away, hover slightly
 *      behind it ready for the return.
 *   4. Else (puck in opponent's half), drift toward a defensive home,
 *      with y biased toward the puck so we're already in line.
 *
 * `aggro` ∈ [0..1] scales how WIDE the attack zone is and how CLOSE the
 * bot hovers behind a retreating puck. Higher = more aggressive.
 */
export function tickBot(
  state: GameState,
  role: Role,
  aggro: number,
): { x: number; y: number } {
  const raw = computeRawTarget(state, role, aggro);

  if (!smoothedInit) {
    smoothedX = raw.x;
    smoothedY = raw.y;
    smoothedInit = true;
  }
  // Pin-breaker sweeps must reach their destination crisply — bypass the
  // filter while the lock is active. Outside that window, slew toward
  // the raw target at a rate proportional to aggro.
  const sweeping = state.tick < sweepLockUntilTick;
  const slew = sweeping ? 1 : 0.25 + aggro * 0.7;
  smoothedX += (raw.x - smoothedX) * slew;
  smoothedY += (raw.y - smoothedY) * slew;
  return { x: smoothedX, y: smoothedY };
}

function computeRawTarget(
  state: GameState,
  role: Role,
  aggro: number,
): { x: number; y: number } {
  const puck = state.puck.pos;
  const puckVel = state.puck.vel;
  const speed = Math.hypot(puckVel.x, puckVel.y);

  const isGuest = role === 'guest';
  const home = isGuest
    ? { x: RINK.width * 0.85, y: RINK.height / 2 }
    : { x: RINK.width * 0.15, y: RINK.height / 2 };

  // Still committed to a sweep from a previous pin? Hold the line.
  if (state.tick < sweepLockUntilTick) {
    return home;
  }

  const inOurHalf = isGuest ? puck.x > RINK.width / 2 : puck.x < RINK.width / 2;
  const movingTowardUs = isGuest ? puckVel.x > 0 : puckVel.x < 0;
  // Distance to our own goal line (in world units along x).
  const distToGoal = isGuest ? RINK.width - puck.x : puck.x;

  // ── WALL-PIN BREAKER ─────────────────────────────────────────────────
  // If the puck is hugging a side wall AND barely moving AND the bot
  // is right next to it, a head-on push just bounces the puck off the
  // wall back into us — classic stalemate. The fix: aim the bot at a
  // far y-edge of the rink. The bot's resulting motion vector is
  // mostly perpendicular to the side wall, and the paddle-velocity
  // transfer (≈90% of paddle speed) imparts that motion to the puck on
  // contact, sliding it along the wall toward rink center. After 1-2
  // ticks the puck is fast enough to fall out of the pinned condition
  // and normal AI resumes.
  const pr = RINK.puckRadius;
  const myPaddle = isGuest ? state.guest.pos : state.host.pos;
  const distBotPuck = Math.hypot(myPaddle.x - puck.x, myPaddle.y - puck.y);
  const onSideWall =
    puck.x < pr * 2.2 ? -1
    : puck.x > RINK.width - pr * 2.2 ? 1
    : 0;
  const pinned = onSideWall !== 0 && speed < 2.5 && distBotPuck < 70;

  if (pinned && inOurHalf) {
    // Sweep AT THE HOME POSITION (interior, mid-y). This gives the bot
    // a motion vector with BOTH x and y components, so paddle-velocity
    // transfer pushes the puck off the wall AND along it. Pure-y sweep
    // (previous approach) failed at corner pins because no x component
    // was being imparted to the puck. Lock for several ticks so the
    // bot actually gets far enough away to disengage before its
    // danger-zone-attack rule re-fires.
    sweepLockUntilTick = state.tick + SWEEP_LOCK_TICKS;
    return home;
  }

  // Attack zone: aggression widens this. Easy bots only attack when the
  // puck is right at the goal mouth; hard bots will go meet it well
  // inside their half.
  const attackZone = RINK.width * (0.20 + aggro * 0.30);
  if (distToGoal < attackZone) {
    return { x: puck.x, y: puck.y };
  }

  if (inOurHalf) {
    if (movingTowardUs || speed < 2) {
      return { x: puck.x, y: puck.y };
    }
    // Puck heading away — hover behind it so a sudden reversal still
    // catches us in position. Aggressive bots hover CLOSER to the puck
    // (smaller offset), defensive bots stay farther back.
    const hoverOffset = 24 * (1 - aggro * 0.8);
    const followX = isGuest ? puck.x + hoverOffset : puck.x - hoverOffset;
    return { x: followX, y: puck.y };
  }

  // Puck is on the opponent's half. Sit near home; aggressive bots
  // line up more strongly with the puck's y so they're ready for a
  // counter.
  const yBlend = 0.5 + aggro * 0.4; // 0=pure home, 1=pure puck.y
  // Aggressive bots also creep forward off the goal line.
  const xCreep = aggro * 0.10 * RINK.width;
  const targetX = isGuest ? home.x - xCreep : home.x + xCreep;
  return {
    x: targetX,
    y: home.y * (1 - yBlend) + puck.y * yBlend,
  };
}
